import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useLanguage } from '@/contexts/LanguageContext';
import { getMonthlyDonationTotals, getDonationYearBounds, type DonationYearBounds } from '@/lib/firebase/queries/donations';
import { monthlyProjectRowsFromTotals, filterProjectYearRows } from '@/lib/linkFinancials';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

/**
 * Rounds a money total for display exactly like mobile's formatCost
 * (mobile/src/screens/marketer/MarketerMonthlyReportScreen.tsx: `Math.round(amount).toLocaleString('en-US')`).
 * Keeping the same rounding here means a fractional sum (e.g. 1000.5) reads as the same figure
 * on both platforms instead of diverging (1000.5 vs 1,001).
 */
const formatMoney = (amount: number) => Math.round(amount).toLocaleString('en-US');

/**
 * Monthly projects report (تقرير المشاريع الشهرية) for the marketer role: gross donations
 * received per month, across all projects — no marketing-net figure, no financial rollup, no
 * case data. Structure (year navigation clamped to the years with data, always-twelve-rows
 * shaping, bold grand-total row) mirrors the admin monthly cost report in
 * AdminHelpManagement.tsx (read-only reference, not modified) — bucketing/shaping itself comes
 * from Task 2's linkFinancials.ts helpers, ported from mobile's caseAdminTools.ts.
 */
const CURRENT_YEAR = new Date().getFullYear();

export function MarketerMonthlyReport() {
  const { t, dir } = useLanguage();

  const [year, setYear] = useState<number>(CURRENT_YEAR);

  // Earliest/latest calendar years with donation data (donations.ts getDonationYearBounds — two
  // single-doc reads, no composite index), fetched once. Restores the years-with-data-only nav
  // bounds this report used before the perf fix replaced the client-side donations scan with
  // server-side aggregation. {earliest: null, latest: null} (empty collection or a failed read)
  // falls through to the disablePrev/disableNext null-checks below: both arrows disabled, year
  // stays at its CURRENT_YEAR initial state.
  const [yearBounds, setYearBounds] = useState<DonationYearBounds>({ earliest: null, latest: null });
  // Guards the one-time seed to the latest year with data so it fires once when the bounds
  // arrive, not on every render (mirrors mobile admin/MonthlyCostScreen.tsx's yearSeeded pattern).
  const [yearSeeded, setYearSeeded] = useState(false);

  useEffect(() => {
    getDonationYearBounds().then(setYearBounds);
  }, []);

  useEffect(() => {
    if (!yearSeeded && yearBounds.latest !== null) {
      setYear(yearBounds.latest);
      setYearSeeded(true);
    }
  }, [yearBounds, yearSeeded]);

  // Per-month {total, count} for the selected year, fetched via server-side sum('amount') +
  // count() aggregation (donations.ts getMonthlyDonationTotals) instead of downloading every
  // donation to bucket client-side — the old `useDonations()` subscription here was an unbounded
  // onSnapshot over 74k+ docs, one half of the production hang on the mobile equivalent screen.
  const [monthlyTotals, setMonthlyTotals] = useState<Record<string, { total: number; count: number }>>({});
  const [totalsLoading, setTotalsLoading] = useState(true);

  // Marketing split settings (settings/treatmentMarketing {x,y}) — same fail-soft read as
  // MarketerProjects.tsx (defaults 0.4/1 until loaded), so the new تكلفة التسويق column matches
  // the projects screen's marketingShare figure for the same money.
  const [marketingX, setMarketingX] = useState(0.4);
  const [marketingY, setMarketingY] = useState(1);

  useEffect(() => {
    const loadMarketingSettings = async () => {
      try {
        const marketingDoc = await getDoc(doc(db, 'settings', 'treatmentMarketing'));
        if (marketingDoc.exists()) {
          const data = marketingDoc.data();
          if (typeof data.x === 'number') setMarketingX(data.x);
          if (typeof data.y === 'number') setMarketingY(data.y);
        }
      } catch (error) {
        console.error('Error loading marketing settings:', error);
      }
    };
    loadMarketingSettings();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTotalsLoading(true);
    getMonthlyDonationTotals(year).then((totals) => {
      if (cancelled) return;
      setMonthlyTotals(totals);
      setTotalsLoading(false);
    });
    return () => { cancelled = true; };
  }, [year]);

  // NOTE (IMPORTANT fix — restores a deleted ruling's rationale): unlike MarketerProjects (which
  // groups actualReceived by existing `links` documents and so drops any donation whose
  // link/project was deleted), this report is built directly from server-side per-month aggregates
  // (getMonthlyDonationTotals) with no per-donation linkId to filter by. It therefore CANNOT run
  // every donation through linkFinancials.ts's filterDonationsToActiveLinks the way the old
  // client-side bucketing did (see that function's own comment: "Fix 2 (controller ruling): the
  // report shows money received FROM PROJECTS, and a deleted project is no longer a project").
  // Monthly totals here DELIBERATELY include donations whose project has since been deleted, and
  // will diverge from the projects screen's totals by that amount — this divergence is accepted,
  // not a bug. filterDonationsToActiveLinks stays exported (additions-only rule) and is exercised
  // by linkFinancials.test.ts, but has no runtime caller anymore.
  const rows = useMemo(
    () => monthlyProjectRowsFromTotals(monthlyTotals, marketingX, marketingY),
    [monthlyTotals, marketingX, marketingY]
  );

  const yearData = useMemo(() => filterProjectYearRows(rows, year), [rows, year]);
  const loading = totalsLoading && Object.keys(monthlyTotals).length === 0;

  const disablePrev = yearBounds.earliest === null || year <= yearBounds.earliest;
  const disableNext = yearBounds.latest === null || year >= yearBounds.latest;

  const headClass = dir === 'rtl' ? 'text-right' : 'text-left';

  return (
    <div className="space-y-4" dir={dir}>
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-lg font-semibold">{t('dashboard.marketer.report.title')}</h3>
              <p className="text-sm text-muted-foreground">{t('dashboard.marketer.report.description')}</p>
            </div>
            {!loading && (
              // Prev/Next disabled at the earliest/latest year with donation data (yearBounds,
              // from getDonationYearBounds) — mirrors the admin monthly cost report's
              // years-with-data-only navigation in AdminHelpManagement.tsx.
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setYear((y) => y - 1)}
                  disabled={disablePrev}
                  aria-label="previous year"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-mono text-base font-semibold tabular-nums w-14 text-center">
                  {year}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setYear((y) => y + 1)}
                  disabled={disableNext}
                  aria-label="next year"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <div className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin me-2" />
                {t('dashboard.marketer.report.loading')}
              </div>
            ) : yearData.totalCount === 0 ? (
              <p className="text-center text-muted-foreground py-8">{t('dashboard.marketer.report.noDonations')}</p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={headClass}>{t('dashboard.marketer.report.columns.month')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.marketer.report.columns.count')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.marketer.report.columns.amount')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.marketer.report.columns.marketingCost')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {yearData.rows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell className="font-medium">{row.key}</TableCell>
                        <TableCell className="text-end font-mono">{row.count.toLocaleString('en-US')}</TableCell>
                        <TableCell className="text-end font-mono">{formatMoney(row.total)}</TableCell>
                        <TableCell className="text-end font-mono">{formatMoney(row.marketingCost)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/40 font-semibold">
                      <TableCell>{t('dashboard.marketer.report.grandTotal')}</TableCell>
                      <TableCell className="text-end font-mono">{yearData.totalCount.toLocaleString('en-US')}</TableCell>
                      <TableCell className="text-end font-mono">{formatMoney(yearData.totalAmount)}</TableCell>
                      <TableCell className="text-end font-mono">{formatMoney(yearData.totalMarketing)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
